import React from "react";

import { ExportButtonGraphql } from "components/actions/ExportButtonGraphql";
import {
  BuyerSignalStatsRequest_SortEnum_0 as BuyerSignalStatsSort,
  BuyerSignalStatsRequest_SortOrderEnum_0 as BuyerSignalStatsSortOrder,
} from "lib/generated/app-service-gql/graphql";
import { useDescribeFramework } from "lib/hooks/api/frameworks/useDescribeFramework";
import { toBuyerSignalStatsFilters } from "lib/hooks/api/frameworks/useSearchFrameworkOrganisations";
import { useExportBuyerSignalStats } from "lib/hooks/api/organisations/useExportBuyerSignalStats";
import { EventDataTypes } from "lib/tracking";
import { TableData } from "./types";
import { LegacyBuyerTableFilters } from "./useLegacyFilterableBuyerTableState";

/**
 * Exports the framework Buyers tab: the same filtered rows the table shows, with the
 * server-side signal and category columns. Selected rows narrow the export to that selection.
 */
export function ExportFrameworkBuyersButton({
  filters,
  selectedBuyers,
  tableData,
  frameworkId,
}: {
  filters: LegacyBuyerTableFilters;
  selectedBuyers?: string[];
  tableData: TableData;
  frameworkId: string;
}) {
  const exportMutation = useExportBuyerSignalStats();

  const {
    totalCount,
    queryResult: { isLoading },
  } = tableData;

  const { data: frameworkData } = useDescribeFramework(frameworkId);
  const frameworkBuyerGuids = frameworkData?.buyers?.map((b) => b.id) ?? [];

  const hasSelection = !!selectedBuyers && selectedBuyers.length > 0;
  const exportCount = hasSelection ? selectedBuyers.length : totalCount ?? 0;

  return (
    <ExportButtonGraphql
      exportMutation={exportMutation}
      exportVariables={{
        request: {
          buyerIds: frameworkBuyerGuids,
          sort: BuyerSignalStatsSort.BuyerName,
          sortOrder: BuyerSignalStatsSortOrder.Asc,
          offset: 0,
          filters: {
            ...toBuyerSignalStatsFilters(filters),
            ...(hasSelection ? { buyerIds: selectedBuyers } : {}),
          },
        },
      }}
      totalResults={exportCount}
      isLoadingTotalResults={isLoading || !frameworkData}
      exportDataType={EventDataTypes.buyer}
    />
  );
}
